/* ============================================================
   IMAGE URL HELPERS
   Product photos live on Cloudinary, which can crop, compress and
   re-encode on the fly. Asking it for a correctly-sized WebP is
   the single biggest performance win available here — a 3 MB
   phone photo becomes ~25 KB for a grid thumbnail.

   Anything that is not a Cloudinary URL passes straight through
   untouched, so nothing breaks if the source ever changes.
   ============================================================ */

const CLOUDINARY_RE = /^(https?:\/\/res\.cloudinary\.com\/[^/]+\/(?:image|video)\/upload)\/(.*)$/;

/** Widths we generate for srcset. Kept short — every extra width
 *  is another cache entry Cloudinary has to build. */
export const GRID_WIDTHS = [200, 300, 400, 600];
export const DETAIL_WIDTHS = [480, 720, 960, 1280];

/**
 * Build a transformed Cloudinary URL.
 *
 * @param {string} url    original secure_url
 * @param {object} [opts]
 * @param {number} [opts.w]        target width in px
 * @param {number} [opts.h]        target height in px
 * @param {string} [opts.crop]     'fill' (default) | 'fit' | 'pad'
 * @param {string} [opts.gravity]  'auto' (default) — keeps the subject centred
 * @param {number|string} [opts.dpr]
 * @returns {string}
 */
export function imgUrl(url, opts = {}) {
  if (!url || typeof url !== 'string') return url || '';

  const m = url.match(CLOUDINARY_RE);
  if (!m) return url;

  const [, base, rest] = m;
  const { w, h, crop = 'fill', gravity = 'auto', dpr } = opts;

  const parts = ['f_auto', 'q_auto:good'];
  if (w) parts.push('w_' + Math.round(w));
  if (h) parts.push('h_' + Math.round(h));
  if (w || h) {
    parts.push('c_' + crop);
    if (crop === 'fill') parts.push('g_' + gravity);
  }
  if (dpr) parts.push('dpr_' + dpr);

  // `rest` may already contain a version segment (v1234/) and/or
  // transformations from a previous call. Strip any transformation
  // we previously injected so calls don't stack up.
  const cleaned = rest.replace(/^(f_auto[^/]*|c_[^/]*|w_[^/]*)\//, '');

  return `${base}/${parts.join(',')}/${cleaned}`;
}

/**
 * A srcset string for responsive loading.
 * @param {string} url
 * @param {number[]} [widths]
 * @param {object} [opts] forwarded to imgUrl
 */
export function imgSrcSet(url, widths = GRID_WIDTHS, opts = {}) {
  if (!url || !CLOUDINARY_RE.test(url)) return undefined;
  return widths.map((w) => `${imgUrl(url, { ...opts, w })} ${w}w`).join(', ');
}

/**
 * A tiny, heavily-blurred version of the image, used as the
 * placeholder that shows while the real photo streams in.
 */
export function imgBlurPlaceholder(url) {
  if (!url || !CLOUDINARY_RE.test(url)) return null;
  return imgUrl(url, { w: 24, crop: 'fill' }) ;
}

/** First usable photo on a product, honouring a selected colour. */
export function productThumb(product, selectedColor) {
  if (!product) return '';
  const byColor = selectedColor && product.colorImages && product.colorImages[selectedColor];
  if (byColor && byColor.length) return byColor[0];
  if (Array.isArray(product.images) && product.images.length) return product.images[0];
  return product.img || '';
}
